import Phaser from "phaser";
import {
  type BackgroundPreset,
  DEFAULT_BACKGROUND_ID,
  NATIVE_HEIGHT,
  NATIVE_WIDTH,
  PALETTE,
  backgroundPresetById,
} from "../config";

/** Stable Phaser texture keys for the edermunizz (Mysterious) forest parallax. */
export const FOREST_LAYER_KEYS = [
  "forest.layer1",
  "forest.layer2",
  "forest.layer3",
  "forest.layer4",
  "forest.layer5",
  "forest.layer6",
  "forest.layer7",
] as const;

/** Texture keys for the "Pixel Art Forest" pack — 4 layers, green/light look. */
export const PIXEL_FOREST_KEYS = [
  "forest.pixel.layer1",
  "forest.pixel.layer2",
  "forest.pixel.layer3",
  "forest.pixel.layer4",
] as const;

/** Texture keys for the "Pixel Art Snowy Forest" pack — 4 layers, cool blue/white look. */
export const SNOWY_FOREST_KEYS = [
  "forest.snowy.layer0",
  "forest.snowy.layer1",
  "forest.snowy.layer2",
  "forest.snowy.layer3",
] as const;

/** Per-layer scroll factors for the 4-layer packs (farthest → nearest). */
export const PIXEL_FOREST_FACTORS = [0.06, 0.2, 0.45, 0.78];
export const SNOWY_FOREST_FACTORS = [0.06, 0.2, 0.45, 0.78];

/**
 * Per-layer horizontal scroll factor (farthest → nearest). Sums to a
 * coherent depth read: the sky barely moves, the rear canopy moves
 * almost at hero pace. We deliberately stop short of foreground
 * scroll factors (>0.7) — the three closest forest plates from the
 * source pack are visually crowded enough to obscure the hero, so
 * they're omitted here to keep the play stage legible (Visual Brief
 * §5: legibility over density).
 */
const FOREST_SCROLL_FACTORS = [0.04, 0.1, 0.18, 0.3, 0.45, 0.62, 0.78];

/**
 * Per-session random scale multiplier range applied on top of the
 * height-fit. **Reverted to 1.0** — runtime scaling on top of the
 * pre-processed 252×216 source introduced bilinear blur that read as a
 * gaussian softness over the canopy. With both min and max at 1.0 the
 * source is rendered at exactly its native resolution; no runtime
 * resampling, no blur. (The top-of-screen sky band visible in the
 * source is a separate concern — to crop that out without runtime
 * scaling, the right fix is a second pre-processing pass that crops
 * the source's top portion before resampling.)
 */
const FOREST_SCALE_JITTER_MIN = 1.0;
const FOREST_SCALE_JITTER_MAX = 1.0;

interface ForestLayer {
  /**
   * One or more Image instances chasing each other in a loop. The
   * count is `ceil(NATIVE_WIDTH / displayWidth) + 1` — enough to
   * always cover the viewport while one image is mid-wrap. With
   * source width 928 scaled to ~252–340 px, that's 3 instances per
   * layer; wider scales fall back to 2.
   */
  imgs: Phaser.GameObjects.Image[];
  factor: number;
  /** Sub-pixel accumulator; rendered position floors to an integer. */
  step: number;
  /** Layer width in display pixels after the source-to-native scale. */
  displayWidth: number;
}

/**
 * Two-layer parallax background (sky far + ruins mid). Drawn as scrolling
 * rectangle bands when no asset pack is present.
 *
 * If the Free Pixel Art Forest pack (edermunizz, CC BY-ND 4.0) is
 * loaded, Parallax upgrades to a 9-layer scrolling Image-pair stack:
 *
 *   - Each source PNG is rendered as **two** `Image`s side by side at
 *     a uniform `setScale(NATIVE_HEIGHT / sourceHeight)`. As the world
 *     scrolls, both images translate left; when the trailing image
 *     reaches the screen edge, it jumps to sit immediately after the
 *     leading image. This produces a seamless infinite scroll without
 *     using `TileSprite`.
 *   - We deliberately avoid `TileSprite` because tile-scaling a
 *     high-resolution source (793 px tall) down to a 216 px viewport
 *     forced the texture sampler to remap source pixels onto output
 *     pixels every frame, producing visible "blink" / shimmer on
 *     translucent canopy edges. Image-pair scrolling samples the
 *     source once per frame at a stable scale and shifts the result
 *     in integer display pixels, eliminating the resample flicker.
 *   - Source textures are switched to LINEAR filtering so the downscale
 *     softens evenly. Game-wide `pixelArt: true` keeps sprite-grid art
 *     pixel-sharp; we only relax it for these specific high-res
 *     backgrounds.
 *
 * Variant B's Background-Upload UGC feature (PRD §6) supplies an optional
 * `BackgroundPreset` whose four color slots tint the **rectangle band**
 * mode. When forest layers are loaded, the preset is ignored visually
 * but the UGC corner-mark still stamps for non-default selections so
 * analysts can see the participant authored a non-baseline run.
 */
export class Parallax {
  private readonly far?: Phaser.GameObjects.Rectangle;
  private readonly farAccent?: Phaser.GameObjects.Rectangle;
  private readonly mid?: Phaser.GameObjects.Rectangle;
  private readonly midDetail: Phaser.GameObjects.Rectangle[] = [];

  /** Forest mode: one pair of Images per layer. Empty when rectangle mode is active. */
  private readonly forest: ForestLayer[] = [];

  private readonly cornerMark?: Phaser.GameObjects.Image;
  private midOffset = 0;

  constructor(scene: Phaser.Scene, preset?: BackgroundPreset) {
    const p = preset ?? backgroundPresetById(DEFAULT_BACKGROUND_ID);

    const allForestPresent = FOREST_LAYER_KEYS.every((k) => scene.textures.exists(k));
    if (allForestPresent) {
      this.buildForest(scene);
    } else {
      this.far = scene.add
        .rectangle(0, 0, NATIVE_WIDTH, Math.floor(NATIVE_HEIGHT * 0.7), p.skyFar)
        .setOrigin(0, 0)
        .setScrollFactor(0);
      this.farAccent = scene.add
        .rectangle(0, Math.floor(NATIVE_HEIGHT * 0.55), NATIVE_WIDTH, 12, p.sky)
        .setOrigin(0, 0)
        .setScrollFactor(0);
      this.mid = scene.add
        .rectangle(
          0,
          Math.floor(NATIVE_HEIGHT * 0.55) + 12,
          NATIVE_WIDTH,
          24,
          p.ruinsMid,
        )
        .setOrigin(0, 0)
        .setScrollFactor(0);
      for (let i = 0; i < 6; i++) {
        const r = scene.add
          .rectangle(0, 0, 16 + ((i * 11) % 14), 16 + ((i * 7) % 12), p.ruinsAccent)
          .setOrigin(0, 1)
          .setScrollFactor(0);
        this.midDetail.push(r);
      }
      this.repositionDetails();
    }

    if (p.id !== DEFAULT_BACKGROUND_ID) {
      this.cornerMark = scene.add
        .image(2, 18, "ugc.corner")
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(5);
    }

    void PALETTE;
  }

  private buildForest(scene: Phaser.Scene): void {
    FOREST_LAYER_KEYS.forEach((key, i) => {
      const src = scene.textures.get(key);
      // NEAREST filter — matches the game-wide `pixelArt: true` mode.
      // With scale jitter reverted to 1.0 and the pre-processed source
      // sized to native (252×216), every output pixel maps to exactly
      // one source pixel; nearest sampling preserves the pixel-art
      // crispness instead of softening it like LINEAR did.
      src.setFilter(Phaser.Textures.FilterMode.NEAREST);
      const srcImg = src.getSourceImage() as { width: number; height: number };
      const baseScale = NATIVE_HEIGHT / srcImg.height;
      // Per-session random scale multiplier — each layer is stretched
      // by 1.0–1.35× so the silhouette reads differently across runs.
      // All instances of a layer share the multiplier so adjacent
      // tiles match.
      const jitter = Phaser.Math.FloatBetween(FOREST_SCALE_JITTER_MIN, FOREST_SCALE_JITTER_MAX);
      const scale = baseScale * jitter;
      const displayWidth = srcImg.width * scale;

      // How many instances do we need? Enough to cover the viewport
      // even when one instance is mid-wrap. With source 928 × scale
      // ≈ 0.272–0.4, displayWidth ≈ 252–370 — smaller than the 384
      // viewport, so a 2-instance "imgA/imgB" pair would expose a gap
      // on the right edge mid-cycle. We always create at least 3
      // instances and let the formula scale up if displayWidth gets
      // even smaller.
      const imgCount = Math.max(2, Math.ceil(NATIVE_WIDTH / displayWidth) + 1);

      // Anchor at the bottom (origin 0, 1; y = NATIVE_HEIGHT) so the
      // pack's baked-in ground line stays pinned to our ground line
      // regardless of scale; bigger-scale layers extend their canopy
      // upward into the sky.
      const depth = -9 + i;
      const spacing = Math.floor(displayWidth);
      const imgs: Phaser.GameObjects.Image[] = [];
      for (let n = 0; n < imgCount; n++) {
        // Pre-position each instance at its tiled offset. Without this,
        // all imgs sit at x=0 and overlap — the screen only renders
        // one tile's worth (~252 px) of forest at start-up. step() will
        // shift them with the world later, but it doesn't run while the
        // hero is in the idle state, so the initial position must
        // already cover the viewport.
        const img = scene.add
          .image(n * spacing, NATIVE_HEIGHT, key)
          .setOrigin(0, 1)
          .setScrollFactor(0)
          .setDepth(depth);
        img.setScale(scale);
        imgs.push(img);
      }

      this.forest.push({
        imgs,
        factor: FOREST_SCROLL_FACTORS[i] ?? 0.5,
        step: 0,
        displayWidth,
      });
    });
  }

  private repositionDetails(): void {
    const baseY = Math.floor(NATIVE_HEIGHT * 0.55) + 12 + 24;
    this.midDetail.forEach((r, i) => {
      r.x = ((i * 67) - this.midOffset) % (NATIVE_WIDTH + 32);
      if (r.x < -32) r.x += NATIVE_WIDTH + 32;
      r.y = baseY;
    });
  }

  /** Call from scene.update() with the world's scrollSpeed * dt. */
  step(scrollPx: number): void {
    if (this.forest.length > 0) {
      this.forest.forEach((l) => {
        l.step += scrollPx * l.factor;
        // Wrap the accumulator so it never grows unbounded.
        if (l.step >= l.displayWidth) l.step -= l.displayWidth;
        // Floor to integer display pixel before painting so we never
        // ask the renderer to sample at a fractional offset (that's
        // what caused the blink in the TileSprite path).
        const offset = Math.floor(l.step);
        // Spacing between adjacent images of the same layer. We use
        // `floor(displayWidth)` (not `ceil`) so adjacent instances
        // OVERLAP by ~1 px instead of being separated by a hairline
        // gap. The overlapping content is identical (same source
        // image at the same scale), so the overlap is invisible —
        // and the seam disappears.
        const spacing = Math.floor(l.displayWidth);
        l.imgs.forEach((img, n) => {
          img.x = -offset + n * spacing;
        });
      });
      return;
    }
    this.midOffset = (this.midOffset + scrollPx * 0.3) % (NATIVE_WIDTH + 32);
    this.repositionDetails();
  }

  /**
   * Tint every layer with a single colour. Used by RunnerScene to
   * visually distinguish nested sub-levels — depth 1 shifts cool,
   * depth 2 shifts warm, etc. — without re-loading textures.
   */
  setTint(color: number): void {
    this.forest.forEach((l) => l.imgs.forEach((img) => img.setTint(color)));
    this.far?.setFillStyle(color);
    this.farAccent?.setFillStyle(color);
    this.mid?.setFillStyle(color);
  }

  /** Remove any active tint, returning layers to their source colours. */
  clearTint(): void {
    this.forest.forEach((l) => l.imgs.forEach((img) => img.clearTint()));
  }

  /**
   * Replace the entire forest layer stack with a different pack at
   * runtime. Destroys the existing images and rebuilds the layer list
   * from `keys` + `scrollFactors`. Used by RunnerScene to swap to a
   * visually distinct background pack when the player enters a
   * sub-level (and back when they exit). Skips silently if the
   * required textures aren't loaded.
   */
  swapForestPack(
    scene: Phaser.Scene,
    keys: ReadonlyArray<string>,
    scrollFactors: ReadonlyArray<number>,
  ): void {
    if (!keys.every((k) => scene.textures.exists(k))) return;

    // Tear down current forest images (rectangle bands stay — they're
    // the placeholder fallback when no pack is loaded, and they're
    // sized for that mode).
    this.forest.forEach((l) => l.imgs.forEach((img) => img.destroy()));
    this.forest.length = 0;

    keys.forEach((key, i) => {
      const src = scene.textures.get(key);
      src.setFilter(Phaser.Textures.FilterMode.NEAREST);
      const srcImg = src.getSourceImage() as { width: number; height: number };
      const scale = NATIVE_HEIGHT / srcImg.height;
      const displayWidth = srcImg.width * scale;
      const imgCount = Math.max(2, Math.ceil(NATIVE_WIDTH / displayWidth) + 1);
      const spacing = Math.floor(displayWidth);

      const depth = -9 + i;
      const imgs: Phaser.GameObjects.Image[] = [];
      for (let n = 0; n < imgCount; n++) {
        const img = scene.add
          .image(n * spacing, NATIVE_HEIGHT, key)
          .setOrigin(0, 1)
          .setScrollFactor(0)
          .setDepth(depth);
        img.setScale(scale);
        imgs.push(img);
      }

      this.forest.push({
        imgs,
        factor: scrollFactors[i] ?? 0.5,
        step: 0,
        displayWidth,
      });
    });
  }

  destroy(): void {
    this.far?.destroy();
    this.farAccent?.destroy();
    this.mid?.destroy();
    this.midDetail.forEach((r) => r.destroy());
    this.forest.forEach((l) => {
      l.imgs.forEach((img) => img.destroy());
    });
    this.cornerMark?.destroy();
  }
}
